import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AuditGoingConcern } from '../../../models/auditGoingConcern';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { LutService } from '../../../services/lut.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-audit-going-concern-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule
  ],
  templateUrl: './audit-going-concern-list.component.html',
  styleUrl: './audit-going-concern-list.component.scss'
})
export class AuditGoingConcernListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  auditGoingConcerns = signal<AuditGoingConcern[]>([]);
  rightItem: AuditGoingConcern | undefined = undefined;
  matchedItems: AuditGoingConcern[] | undefined = undefined;

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadAuditGoingConcerns();
  }

  loadAuditGoingConcerns(): void {
    this.spinnerService.show();
    this.lutService.getAuditGoingConcerns().subscribe({
      next: (data) => {
        // Sort alphabetically by name before setting
        const sortedData = this.sortAuditGoingConcerns(data);
        this.auditGoingConcerns.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading audit going concerns:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortAuditGoingConcerns(auditGoingConcerns: AuditGoingConcern[]): AuditGoingConcern[] {
    return [...auditGoingConcerns].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateAuditGoingConcern(): void {
    this.lookupTable.startAdd();
  }

  onItemSelected(auditGoingConcern: AuditGoingConcern): void {
  }

  onEditAuditGoingConcern(auditGoingConcern: AuditGoingConcern): void {
  }

  onDeleteAuditGoingConcern(auditGoingConcern: AuditGoingConcern): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(auditGoingConcern.name)
      .subscribe(result => {
        if (result) {
          // Store original list for rollback in case of error
          const originalAuditGoingConcerns = this.auditGoingConcerns();

          // 1. Optimistically remove from local state immediately
          this.auditGoingConcerns.update(auditGoingConcerns => auditGoingConcerns.filter(agc => agc.id !== auditGoingConcern.id));

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteAuditGoingConcern(auditGoingConcern.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Audit going concern deleted successfully');
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting audit going concern:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original list
              this.auditGoingConcerns.set(originalAuditGoingConcerns);
              this.toast.error(`<strong>Failed to delete Audit Going Concern:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: AuditGoingConcern, newName: string} | {item: AuditGoingConcern, newValue: string, columnKey: string}): void {

    // Store original item for rollback in case of error
    const originalItem = { ...event.item };

    // Prepare updated item
    let updatedItem: AuditGoingConcern;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.auditGoingConcerns.update(auditGoingConcerns => {
      const updated = auditGoingConcerns.map(agc => agc.id === event.item.id ? updatedItem : agc);
      return this.sortAuditGoingConcerns(updated);
    });

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateAuditGoingConcern(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Audit going concern updated successfully');
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.auditGoingConcerns.update(auditGoingConcerns => {
          const updated = auditGoingConcerns.map(agc => agc.id === event.item.id ? response : agc);
          return this.sortAuditGoingConcerns(updated);
        });
      },
      error: (error) => {
        console.error('Error updating audit going concern:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.auditGoingConcerns.update(auditGoingConcerns =>
          auditGoingConcerns.map(agc => agc.id === event.item.id ? originalItem : agc)
        );
        this.toast.error(`<strong>Failed to update Audit Going Concern:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newAuditGoingConcern: AuditGoingConcern = { id: tempId, name: event['name'], systemReserved: false };

    // 1. Optimistically add to local state immediately and sort
    this.auditGoingConcerns.update(auditGoingConcerns => this.sortAuditGoingConcerns([...auditGoingConcerns, newAuditGoingConcern]));

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createAuditGoingConcern({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Audit going concern created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.auditGoingConcerns.update(auditGoingConcerns => {
          const updated = auditGoingConcerns.map(agc => agc.id === tempId ? response : agc);
          return this.sortAuditGoingConcerns(updated);
        });
      },
      error: (error) => {
        console.error('Error creating audit going concern:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.auditGoingConcerns.update(auditGoingConcerns =>
          auditGoingConcerns.filter(agc => agc.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Audit Going Concern:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onReadyToMatch(event: {rightItem?: AuditGoingConcern, matchedItems?: AuditGoingConcern[]}): void {
    this.rightItem = event.rightItem;
    this.matchedItems = event.matchedItems;
  }

  onAuditGoingConcernCorrected(): void {
    // Validate that we have the required data
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select items to merge');
      return;
    }

    // Prepare the bulk merge request
    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    // Show confirmation dialog
    const itemsToMerge = this.matchedItems.map(item => item.name).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.name}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        // Store original list for rollback in case of error
        const originalAuditGoingConcerns = this.auditGoingConcerns();

        // 1. Optimistically remove merged items from local state
        this.auditGoingConcerns.update(auditGoingConcerns =>
          auditGoingConcerns.filter(agc => !bulkMergeRequest.leftIds.includes(agc.id))
        );

        // Clear the selection state
        this.rightItem = undefined;
        this.matchedItems = undefined;

        // 2. Call backend API
        this.spinnerService.show();
        this.lutService.mergeAuditGoingConcernsBulk(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Audit going concerns merged successfully');
            // 3. On success: Clear the lookup table's internal matching state and reload the list
            this.lookupTable.clearMatchingState();
            this.loadAuditGoingConcerns();
          },
          error: (error) => {
            console.error('Error merging audit going concerns:', error);
            this.spinnerService.hide();
            // 4. On error: Restore original list
            this.auditGoingConcerns.set(originalAuditGoingConcerns);
            // Restore selection state for retry
            this.rightItem = originalAuditGoingConcerns.find(agc => agc.id === bulkMergeRequest.rightId);
            this.matchedItems = originalAuditGoingConcerns.filter(agc => bulkMergeRequest.leftIds.includes(agc.id));
            this.toast.error(`<strong>Failed to merge Audit Going Concerns:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }
}
